<template>
  <div class="card content-wrap-card">
    <div class="section-head">
      <h2 class="section-title">inapi·mq 발송 경로 (서비스 전체)</h2>
      <a-button size="small" :loading="loading" @click="load">다시 읽기</a-button>
    </div>
    <p class="section-hint">
      crm 이 아닌 inapi·mq 가 직접 보내는 알림톡·문자의 길입니다. 회사별 설정은 없고 서비스 전체에 적용됩니다.
      바꾼 값은 {{ SWITCH_PROPAGATION_SECONDS }}초 안에 모든 서버의 새 발송부터 적용되고, 문제가 생기면 v1 으로 바로
      되돌릴 수 있습니다. DKT 가 옛 API 를 끄면(10/31 예정) v1 은 동작하지 않습니다.
    </p>
    <div v-for="s in SERVICE_SWITCHES" :key="s.key" class="route-row">
      <span class="route-channel">{{ s.label }}</span>
      <a-radio-group
        :value="states[s.key] && states[s.key].route"
        option-type="button"
        button-style="solid"
        :disabled="!loaded || saving"
        @change="(e) => confirmChange(s, e.target.value)"
      >
        <a-radio-button value="v1">{{ s.v1 }}</a-radio-button>
        <a-radio-button value="v2">{{ s.v2 }}</a-radio-button>
      </a-radio-group>
      <span class="route-meta">
        <template v-if="states[s.key] && states[s.key].modifyAt">
          마지막 변경 {{ formatTime(states[s.key].modifyAt) }}<template v-if="states[s.key].updatedBy"> · {{ states[s.key].updatedBy }}</template>
        </template>
        <template v-else>아직 바꾼 적 없음(v1)</template>
      </span>
      <div class="route-scope">대상: {{ s.scope }}</div>
    </div>

    <h3 class="history-title">변경 이력 (최근 30건)</h3>
    <a-table
      :columns="historyColumns"
      :data-source="history"
      :pagination="false"
      size="small"
      row-key="key"
      :locale="{ emptyText: '아직 바꾼 적이 없습니다' }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'at'">{{ formatTime(record.at) }}</template>
      </template>
    </a-table>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { message, Modal } from "ant-design-vue";
import dayjs from "dayjs";
import { serviceRouteApi } from "../serviceRouteApi";
import { SERVICE_SWITCHES, SWITCH_PROPAGATION_SECONDS, switchStates, historyRows } from "../serviceRouteSwitch";

const formatTime = (t) => (t ? dayjs(t).format("YYYY-MM-DD HH:mm:ss") : "-");

const historyColumns = [
  { title: "시각", key: "at", width: 170 },
  { title: "서비스", dataIndex: "service", key: "service", width: 140 },
  { title: "바꾼 값", dataIndex: "change", key: "change", width: 110 },
  { title: "바꾼 사람", dataIndex: "by", key: "by" },
];

export default {
  name: "ServiceRouteSwitches",
  setup() {
    // 서버 값이 진실: 쓰기 뒤에는 늘 다시 읽고, 화면 상태를 미리 바꾸지 않는다(발송 경로 화면과 같은 규칙)
    const loaded = ref(false);
    const loading = ref(false);
    const saving = ref(false);
    const states = ref(switchStates(undefined));
    const history = ref([]);

    const load = async () => {
      loading.value = true;
      try {
        const { data } = await serviceRouteApi.list();
        states.value = switchStates(data);
        history.value = historyRows(data);
        loaded.value = true;
      } catch (error) {
        loaded.value = false;
        console.error("inapi·mq 발송 경로 읽기 실패:", error);
        message.error("inapi·mq 발송 경로를 읽지 못했습니다. '다시 읽기'를 눌러 주세요.");
      } finally {
        loading.value = false;
      }
    };

    const confirmChange = (s, route) => {
      const toV1 = route === "v1";
      Modal.confirm({
        title: `${s.label} → ${toV1 ? s.v1 : s.v2}`,
        content: toV1
          ? `옛 API 로 되돌립니다. ${SWITCH_PROPAGATION_SECONDS}초 안에 모든 서버의 새 발송부터 적용됩니다. DKT 가 옛 API 를 끈 뒤에는 v1 으로 보내면 실패합니다.`
          : `새 경로로 바꿉니다. ${SWITCH_PROPAGATION_SECONDS}초 안에 모든 서버의 새 발송부터 적용됩니다. 문제가 생기면 v1 으로 되돌리세요.`,
        okText: "바꾸기",
        cancelText: "취소",
        onOk: async () => {
          saving.value = true;
          try {
            await serviceRouteApi.save(s.key, route);
            message.success(`저장했습니다. ${SWITCH_PROPAGATION_SECONDS}초 안에 모든 서버에 적용됩니다.`);
          } catch (error) {
            console.error("inapi·mq 발송 경로 저장 실패:", error);
            message.error("저장하지 못했습니다. 지금 값을 다시 읽었으니 확인한 뒤 다시 시도해 주세요.");
          } finally {
            saving.value = false;
            await load();
          }
        },
      });
    };

    onMounted(load);

    return { SERVICE_SWITCHES, SWITCH_PROPAGATION_SECONDS, states, history, historyColumns, loaded, loading, saving, load, confirmChange, formatTime };
  },
};
</script>

<style scoped>
.content-wrap-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  padding: 24px;
}

.section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 8px;
}

.section-hint {
  font-size: 13px;
  color: #6c757d;
  margin: 8px 0 12px;
}

.route-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.route-row:last-of-type {
  border-bottom: none;
}

.route-channel {
  width: 110px;
  font-weight: 600;
}

.route-meta {
  font-size: 12px;
  color: #8c8c8c;
}

.history-title {
  font-size: 14px;
  font-weight: 700;
  margin: 18px 0 8px;
}

.route-scope {
  flex-basis: 100%;
  font-size: 12px;
  color: #8c8c8c;
  padding-left: 122px;
}
</style>
